import React, { useState, useEffect, useRef } from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DemoVideoFrame from '../../components/home/DemoVideoFrame';
import { appendTrackingParams } from '../../utils/tracking';

const AccountingHeroSection = () => {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const mountedAtRef = useRef(Date.now());

  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com/').replace(/\/$/, '');

  const isPrivacyRef = new URLSearchParams(window.location.search).get('ref') === 'privacy';

  const headline = isPrivacyRef
    ? 'Accounting Without the Spyware.'
    : 'Accounting Built for Privacy. No Subscription. No Lock-In.';

  useEffect(() => {
    mountedAtRef.current = Date.now();
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const elapsed = Date.now() - (mountedAtRef.current || Date.now());
    if (elapsed < 800) {
      setError('Please wait a moment before submitting.');
      return;
    }
    if (hp && hp.trim().length > 0) {
      return;
    }

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const r = await fetch('/api/waitlist/accounting-pro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, source: isPrivacyRef ? 'privacy' : 'accounting_hero' }),
      });
      const isJson = (r.headers.get('content-type') || '').includes('application/json');
      const j = isJson ? await r.json().catch(() => ({})) : null;
      if (r.ok && (j?.success || j?.ok)) {
        setSuccess(true);
        setEmail('');
      } else {
        let detail = '';
        try { detail = isJson ? (j?.error || JSON.stringify(j)) : await r.text(); } catch {}
        setError(`Request failed${detail ? ': ' + detail : ''}`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const videoRef = useRef(null);
  const [bubbleState, setBubbleState] = useState({ showLeft: false, showRight: false });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let rafId;

    const updateBubbles = () => {
      const current = video.currentTime || 0;
      const duration = video.duration || 0;
      const showLeft = current >= 1 && current < 6;
      const showRight = current >= 21 && current < Math.max(24, duration);
      setBubbleState((prev) => {
        if (prev.showLeft === showLeft && prev.showRight === showRight) return prev;
        return { showLeft, showRight };
      });
      rafId = requestAnimationFrame(updateBubbles);
    };

    const handlePlay = () => { cancelAnimationFrame(rafId); rafId = requestAnimationFrame(updateBubbles); };
    const handlePause = () => { cancelAnimationFrame(rafId); };
    const handleEnded = () => { setBubbleState({ showLeft: false, showRight: false }); };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    if (!video.paused) handlePlay();

    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 ai-particles opacity-40"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-8 pt-28 sm:pt-24 md:pb-10 md:pt-28 lg:px-8 lg:pt-14 xl:max-w-[1600px] 2xl:max-w-[1800px]">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.7fr] lg:items-start">

          {/* Left Content */}
          <div className="space-y-8 text-center lg:mt-0 lg:text-left">
            <div className="space-y-6">
              <h1 className="hub-font-title animate-fade-in text-[clamp(1.8rem,5.5vw,5rem)] font-light leading-tight tracking-[-0.0125em] drop-shadow-sm">
                <span className="block text-[#0372c4]">
                  {headline}
                </span>
              </h1>

              <p className="text-xl hero-subtitle text-muted-foreground max-w-2xl leading-relaxed">
                Drop in your CSV, automate your bookkeeping locally, and keep your financial data strictly offline.
              </p>
            </div>

            {/* Waitlist Form */}
            <div className="flex flex-col gap-3 max-w-md mx-auto lg:mx-0">
              <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  name="hp"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div className="relative flex-1">
                  <Icon name="Mail" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full h-11 pl-10 pr-4 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  />
                </div>
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  disabled={submitting}
                  className="bg-brand text-slate-900 shadow-2xl shadow-brand/25 hover:shadow-brand/40 transform hover:scale-105 transition-all duration-300 h-11 px-6 text-sm sm:text-base whitespace-nowrap"
                  iconName="Zap"
                  iconPosition="left"
                >
                  {submitting ? 'Joining...' : 'Join the Pro Waitlist'}
                </Button>
              </form>

              {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
              {success && (
                <div role="status" className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  Thanks! You're on the Pro waitlist. We'll email you when it opens.
                </div>
              )}

              <a
                href={appendTrackingParams(appUrl)}
                className="inline-flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors self-center lg:self-start"
              >
                <Icon name="Smartphone" size={16} />
                Try Basic (Free)
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 pt-3 sm:pt-4">
              <div className="flex items-center space-x-3 text-base sm:text-lg text-slate-800 font-semibold">
                <div className="p-2 bg-green-400/15 rounded-full">
                  <Icon name="Shield" size={20} className="text-green-500" />
                </div>
                <span className="font-semibold tracking-tight">No Open Banking Required</span>
              </div>
              <div className="flex items-center space-x-3 text-base sm:text-lg text-slate-800 font-semibold">
                <div className="p-2 bg-blue-400/15 rounded-full">
                  <Icon name="Clock" size={20} className="text-blue-500" />
                </div>
                <span className="font-semibold tracking-tight">5-Minute Setup</span>
              </div>
              <div className="flex items-center space-x-3 text-base sm:text-lg text-slate-800 font-semibold">
                <div className="p-2 bg-emerald-400/15 rounded-full">
                  <Icon name="BadgeCheck" size={20} className="text-emerald-500" />
                </div>
                <span className="font-semibold tracking-tight">
                  HMRC Recognised{' '}
                  <span aria-hidden="true">•</span>{' '}
                  MTD ITSA + MTD VAT
                </span>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative flex justify-center lg:justify-end">
            <DemoVideoFrame className="w-full max-w-2xl lg:max-w-none" ref={videoRef} />

            <div className="pointer-events-none absolute inset-0">
              <div
                className={`hint-bubble hint-left ${bubbleState.showLeft ? 'hint-visible' : ''}`}
                style={{ top: '9%', left: '3%' }}
              >
                Attach a receipt
              </div>
              <div
                className={`hint-bubble hint-right ${bubbleState.showRight ? 'hint-visible' : ''}`}
                style={{ bottom: '8%', right: '3%' }}
              >
                Smart categorization
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        .hint-bubble {
          position: absolute;
          display: flex; align-items: center; justify-content: center; text-align: center;
          width: clamp(88px, 8.2vw, 138px); height: clamp(88px, 8.2vw, 138px);
          padding: 12px; line-height: 1.2;
          background: rgba(181,193,217,0.70);
          color: #1e293b;
          border: 1px solid rgba(128,138,157,0.85);
          border-radius: 9999px;
          backdrop-filter: blur(10px);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.30), 0 10px 28px rgba(30,41,59,0.14);
          font-weight: 700;
          font-size: 1.05rem;
          letter-spacing: 0.2px;
          opacity: 0;
          --bubble-shift: translateX(0%);
          --edge-outer: 0px;
        }
        .hint-left {
          --bubble-shift: translateX(-28%);
          --edge-outer: -50px;
          width: clamp(62px, 5.8vw, 97px);
          height: clamp(62px, 5.8vw, 97px);
        }
        .hint-right {
          --bubble-shift: translateX(28%);
          --edge-outer: 50px;
          background: rgba(163,177,207,0.72);
        }
        .hint-visible {
          opacity: 1;
          transform: translateX(var(--edge-outer)) var(--bubble-shift) translateY(0) scale(1);
        }
        .hint-bubble {
          transition: opacity 0.6s ease, transform 0.6s ease;
          transform: translateX(var(--edge-outer)) var(--bubble-shift) translateY(6px) scale(0.98);
        }
        .hero-subtitle {
          font-size: clamp(1.05rem, 2.1vw, 1.25rem);
        }
      `}</style>
    </section>
  );
};

export default AccountingHeroSection;
